import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, FunnelIcon, TagIcon } from '@heroicons/react/24/outline';

/**
 * PageTags - Standardized tags and filters component
 * 
 * Used for displaying contextual tags, filters, and categories.
 * Supports selection, removal, and add new tag functionality.
 * 
 * Features:
 * - Multiple tag types (filter, status, category)
 * - Interactive selection/deselection
 * - Add custom tags
 * - Remove tags
 * - Search/filter functionality
 * - Animated transitions
 * - Responsive design
 * 
 * @example
 * <PageTags
 *   tags={[
 *     { id: 'all', label: 'All Users', type: 'filter', active: true },
 *     { id: 'active', label: 'Active', type: 'status', count: 45 },
 *     { id: 'admin', label: 'Admin', type: 'role', removable: true }
 *   ]}
 *   allowAdd={true}
 *   onTagClick={(tag) => console.log('Tag clicked:', tag)}
 *   onTagRemove={(tagId) => console.log('Tag removed:', tagId)}
 *   onTagAdd={(label) => console.log('Tag added:', label)}
 * />
 */
const PageTags = ({
  tags = [],
  type = 'default', // 'default', 'filter', 'pills', 'chips'
  size = 'default', // 'sm', 'default', 'lg'
  allowAdd = false,
  allowRemove = false,
  showSearch = false,
  maxVisible = null,
  className = '',
  onTagClick = null,
  onTagRemove = null,
  onTagAdd = null
}) => {
  const [newTagInput, setNewTagInput] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter tags based on search term
  const filteredTags = tags.filter(tag => 
    !searchTerm || tag.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show limited tags + "show more" if needed
  const visibleTags = maxVisible && filteredTags.length > maxVisible 
    ? filteredTags.slice(0, maxVisible) 
    : filteredTags;
  const hasHiddenTags = maxVisible && filteredTags.length > maxVisible;

  // Get tag styling based on type and state
  const getTagClasses = (tag) => {
    const baseClasses = 'inline-flex items-center gap-2 border transition-all duration-200 cursor-pointer';
    
    switch (type) {
      case 'filter':
        return `${baseClasses} badge badge-md badge-outline sm:badge-lg ${
          tag.active 
            ? 'badge-primary border-primary text-primary bg-primary/10' 
            : 'border-base-300 text-base-content/70 hover:border-base-content/50'
        }`;
      
      case 'pills':
        return `${baseClasses} px-2 py-1 rounded-full text-xs sm:px-3 sm:text-sm ${
          tag.active 
            ? 'bg-primary text-primary-content border-primary' 
            : 'bg-base-200 text-base-content border-base-300 hover:bg-base-300'
        }`;
      
      case 'chips':
        return `${baseClasses} px-2 py-1.5 rounded-lg text-xs font-medium sm:px-3 sm:py-2 sm:text-sm ${
          tag.active 
            ? 'bg-primary text-primary-content shadow-sm' 
            : 'bg-base-100 border-base-300 hover:border-primary/50'
        }`;
      
      case 'default':
      default:
        return `${baseClasses} badge ${
          tag.color ? `badge-${tag.color}` : 'badge-neutral'
        } ${tag.active ? 'badge-outline' : ''}`;
    }
  };

  // Handle tag click
  const handleTagClick = (tag) => {
    if (onTagClick) {
      onTagClick(tag);
    }
  };

  // Handle tag removal
  const handleTagRemove = (e, tagId) => {
    e.stopPropagation();
    if (onTagRemove) {
      onTagRemove(tagId);
    }
  };

  // Handle adding new tag
  const handleAddTag = () => {
    if (newTagInput.trim() && onTagAdd) {
      onTagAdd(newTagInput.trim());
      setNewTagInput('');
      setIsAddingTag(false);
    }
  };

  // Render individual tag
  const renderTag = (tag) => (
    <motion.div
      key={tag.id}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.2 }}
      onClick={() => handleTagClick(tag)}
      className={getTagClasses(tag)}
    >
      {tag.icon && <tag.icon className="w-3 h-3" />}
      <span>{tag.label}</span>
      {tag.count !== undefined && (
        <span className="text-xs opacity-70">({tag.count})</span>
      )}
      {(allowRemove || tag.removable) && onTagRemove && (
        <button
          onClick={(e) => handleTagRemove(e, tag.id)}
          className="ml-1 hover:opacity-70"
          aria-label={`Remove ${tag.label}`}
        >
          <XMarkIcon className="w-3 h-3" />
        </button>
      )}
    </motion.div>
  );

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {/* Search and Add Controls */}
      {(showSearch || allowAdd) && (
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {showSearch && (
          <div className="relative w-full sm:w-auto">
            <input
              type="text"
              placeholder="Search tags..."
              className="input input-bordered input-sm pr-8 w-full sm:w-auto"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FunnelIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/50" />
          </div>
        )}
        
        {allowAdd && !isAddingTag && (
          <button
            onClick={() => setIsAddingTag(true)}
            className="btn btn-sm btn-outline w-full sm:w-auto"
          >
            <TagIcon className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Add Tag</span>
            <span className="sm:hidden">Add</span>
          </button>
        )}
      </div>
      )}

      {/* Add Tag Input */}
      <AnimatePresence>
        {isAddingTag && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex gap-2"
          >
            <input
              type="text"
              placeholder="Enter tag name..."
              className="input input-bordered input-sm flex-1"
              value={newTagInput}
              onChange={(e) => setNewTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddTag();
                } else if (e.key === 'Escape') {
                  setIsAddingTag(false);
                  setNewTagInput('');
                }
              }}
              autoFocus
            />
            <button
              onClick={handleAddTag}
              className="btn btn-sm btn-primary"
              disabled={!newTagInput.trim()}
            >
              Add
            </button>
            <button
              onClick={() => {
                setIsAddingTag(false);
                setNewTagInput('');
              }}
              className="btn btn-sm btn-ghost"
            >
              Cancel
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tags Display */}
      <div className="flex flex-wrap gap-1 sm:gap-2">
        <AnimatePresence>
          {visibleTags.map((tag) => renderTag(tag))}
        </AnimatePresence>

        {/* Show More/Less Button */}
        {hasHiddenTags && (
          <button
            onClick={() => setSearchTerm('')}
            className={`btn btn-sm btn-ghost ${
              type === 'filter' ? 'badge badge-outline' : ''
            }`}
          >
            +{filteredTags.length - maxVisible} more
          </button>
        )}

        {/* No tags state */}
        {filteredTags.length === 0 && (
          <div className="text-sm text-base-content/50 italic">
            {searchTerm ? 'No tags match your search' : 'No tags available'}
          </div>
        )}
      </div>
    </div>
  );
};

// Preset configurations for common use cases
PageTags.presets = {
  // Filter tags for data tables
  filters: (items) => ({
    type: 'filter',
    tags: items.map(item => ({
      ...item,
      type: 'filter'
    }))
  }),

  // Status indicators
  status: (items) => ({
    type: 'pills',
    tags: items.map(item => ({
      ...item,
      type: 'status'
    }))
  }),

  // Category tags
  categories: (items) => ({
    type: 'chips',
    allowAdd: true,
    tags: items.map(item => ({
      ...item,
      type: 'category'
    }))
  }),

  // Simple tag display
  simple: (items) => ({
    type: 'default',
    tags: items
  })
};

export default PageTags;